import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Facebook, Twitter, Instagram, Linkedin, Heart, ArrowUpRight,
  Stethoscope, Building2, Pill, Droplets, Youtube, Shield, Clock, MapPin, Loader2
} from "lucide-react";
import { motion } from "framer-motion";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import LogoImage from "@/components/ui/logo-image";
import { useAllModulesConfig } from "@/hooks/useAuthConfig";
import { isAdminPreview } from "@/hooks/useModuleVisibility";
import { toast } from "sonner";
import { bridgeApi } from "@/lib/bridgeApi";
import { usePopularCityCategories } from "@/hooks/useLocations";

interface PlatformStats {
  hospitals: number;
  doctors: number;
  pharmacies: number;
  blood_banks: number;
}

const itemVariants = {
  hidden: { opacity: 0, y: 16, filter: 'blur(4px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] } },
};

const Footer = () => {
  const { siteName, logoHeightFooter, socialFacebook, socialInstagram, socialTwitter, socialYoutube, socialLinkedin } = useSiteSettings();

  // Module visibility — hide footer links for HIDDEN/COMING_SOON modules
  const moduleConfig = useAllModulesConfig();
  const adminPreview = isAdminPreview();
  const isVisible = (key?: string) => {
    if (!key) return true;
    if (adminPreview) return true;
    const status = moduleConfig?.[key]?.visibility_status ?? 'LIVE';
    return status === 'LIVE';
  };

  const footerLinks = {
    quickLinks: [
      { name: "Find Hospitals", href: "/listings?category=1", moduleKey: 'hospital' },
      { name: "Search Doctors", href: "/listings?category=2", moduleKey: 'doctor' },
      { name: "Nearby Pharmacies", href: "/listings?category=3", moduleKey: 'medical_store' },
      { name: "HomeCare Services", href: "/homecare", moduleKey: 'homecare' },
      { name: "Ambulance Services", href: "/ambulance", moduleKey: 'ambulance' },
    ].filter(l => isVisible(l.moduleKey)),
    company: [
      { name: "About Us", href: "/about" },
      { name: "Contact Us", href: "/contact" },
      // No dedicated /partner or /advertise pages exist yet — route both
      // partnership/advertising inquiries to Contact instead of a 404.
      { name: "Partner With Us", href: "/contact" },
      { name: "Advertise", href: "/contact" },
      { name: "Health Camps", href: "/health-camps", moduleKey: 'health_camp' as string | undefined },
    ].filter(l => isVisible((l as any).moduleKey)),
    legal: [
      { name: "Terms of Service", href: "/terms" },
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Refund Policy", href: "/refund" },
      { name: "Cookie Policy", href: "/cookies" },
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: socialFacebook, label: "Facebook" },
    { icon: Twitter, href: socialTwitter, label: "Twitter" },
    { icon: Instagram, href: socialInstagram, label: "Instagram" },
    { icon: Youtube, href: socialYoutube, label: "YouTube" },
    { icon: Linkedin, href: socialLinkedin, label: "LinkedIn" },
  ];

  // Real counts (active + verified providers only) — replaces the previous
  // hardcoded, static "500+ Hospitals" style numbers that never reflected
  // actual platform size. See handleGetPlatformStats() in bridgeapi/api.php.
  const { data: platformStats } = useQuery({
    queryKey: ['platform-stats'],
    queryFn: async () => {
      // bridgeApi.call() already unwraps the `data` field of {success, data}
      // responses (see bridgeApi.ts) — reading `.data` again here was always
      // undefined, which is why the footer showed "— Hospitals" etc. instead
      // of real counts despite the API call itself succeeding.
      return await bridgeApi.call<PlatformStats>('platform_stats', {});
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  const fmtCount = (n?: number) => (typeof n === 'number' ? n.toLocaleString('en-IN') : '—');

  const serviceHighlights = [
    { icon: Building2, label: `${fmtCount(platformStats?.hospitals)} Hospitals`, color: "text-blue-200" },
    { icon: Stethoscope, label: `${fmtCount(platformStats?.doctors)} Doctors`, color: "text-emerald-300" },
    { icon: Pill, label: `${fmtCount(platformStats?.pharmacies)} Pharmacies`, color: "text-amber-300" },
    { icon: Droplets, label: `${fmtCount(platformStats?.blood_banks)} Blood Banks`, color: "text-rose-300" },
  ];

  // Real, crawlable links to /doctors/{city} etc. — same combos the homepage
  // section shows, kept out of the main columns since the footer's 4-column
  // grid has no room for a variable-length list like this.
  const { data: popularCityCombos } = usePopularCityCategories(10);

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterLoading(true);
    try {
      await bridgeApi.post('newsletter_subscribe', { email: newsletterEmail.trim() });
      setNewsletterEmail("");
      toast.success("Subscribed!", {
        description: "You'll receive health tips & updates from PataKare.",
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to subscribe. Please try again.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <footer className="relative overflow-hidden" style={{ background: 'var(--ui-footer-bg, linear-gradient(135deg, hsl(213 69% 25%), hsl(213 60% 32%), hsl(152 62% 30%)))' }}>
      <div className="absolute inset-0 bg-white/5" />

      {/* Decorative orb */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Stats bar */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden mt-10 sm:mt-12"
        >
          {serviceHighlights.map((item) => (
            <motion.div
              key={item.label}
              variants={itemVariants}
              className="flex items-center justify-center gap-2.5 py-4 sm:py-5 px-3 bg-white/[0.03] hover:bg-white/[0.07] transition-colors"
            >
              <item.icon className={`w-5 h-5 ${item.color} flex-shrink-0`} />
              <span className="text-white/90 text-xs sm:text-sm font-medium">{item.label}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Main grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10 pt-10 sm:pt-12 pb-8"
        >
          {/* Brand Column */}
          <motion.div variants={itemVariants} className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center justify-center bg-white/95 rounded-xl px-3 py-2 shadow-sm">
                <LogoImage 
                  alt={siteName || 'PataKare'} 
                  style={{ height: `${logoHeightFooter || 56}px` }}
                  className="w-auto object-contain"
                />
              </div>
              <div>
                <p className="text-white/60 text-xs">India's Trusted Healthcare Directory</p>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs mb-5">
              Find verified hospitals, doctors, pharmacies, diagnostic centers, and blood banks near you.
            </p>

            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-9 h-9 rounded-lg bg-white/[0.07] border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 transition-all"
                >
                  <social.icon className="w-4 h-4" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/65 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group">
                    <span className="w-0 group-hover:w-2 h-px bg-white/60 transition-all duration-200" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">Company</h4>
            <ul className="space-y-2.5">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/65 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group">
                    <span className="w-0 group-hover:w-2 h-px bg-white/60 transition-all duration-200" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal + Newsletter */}
          <motion.div variants={itemVariants} className="lg:col-span-4">
            <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">Legal</h4>
            <ul className="flex flex-wrap gap-x-4 gap-y-2 mb-6">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/65 hover:text-white text-sm transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>

            {/* Newsletter inline */}
            <div className="p-4 rounded-xl bg-white/[0.06] border border-white/10">
              <h5 className="text-sm font-semibold text-white mb-1">Get Health Tips & Updates</h5>
              <p className="text-white/50 text-xs mb-3">Subscribe for healthcare news and offers.</p>
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  placeholder="Your email"
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  disabled={newsletterLoading}
                  className="flex-1 min-w-0 px-3.5 py-2 rounded-lg bg-white/[0.08] border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all text-sm disabled:opacity-60"
                />
                <motion.button
                  type="submit"
                  disabled={newsletterLoading}
                  whileHover={{ scale: newsletterLoading ? 1 : 1.03 }}
                  whileTap={{ scale: newsletterLoading ? 1 : 0.97 }}
                  className="px-4 py-2 font-semibold rounded-lg text-sm text-white shrink-0 flex items-center gap-1.5 disabled:opacity-70"
                  style={{ background: 'linear-gradient(135deg, hsl(33 100% 50%), hsl(33 100% 58%))' }}
                >
                  {newsletterLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Subscribe <ArrowUpRight className="w-3.5 h-3.5" /></>}
                </motion.button>
              </form>
              {/* DPDP Act passive disclosure — same approach as the cookie-consent
                  decision (no blocking checkbox for low-sensitivity, single-purpose
                  data collection like an email newsletter signup; the subscribe
                  action itself is the specific, unambiguous consent). */}
              <p className="text-white/35 text-[11px] mt-2">
                By subscribing, you agree to our <Link to="/privacy" className="underline hover:text-white/60">Privacy Policy</Link>.
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Popular Cities — real internal links so crawlers reach /doctors/{city}
            etc. pages, which otherwise have no on-site link pointing at them */}
        {popularCityCombos && popularCityCombos.length > 0 && (
          <div className="py-5 border-t border-white/[0.08]">
            <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">Popular Searches</h4>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {popularCityCombos.map((c) => (
                <Link
                  key={`${c.type_code}-${c.city_name}`}
                  to={c.path}
                  className="text-white/50 hover:text-white text-xs transition-colors"
                >
                  {c.category_label} in {c.city_name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="py-5 border-t border-white/[0.08]"
        >
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-white/45 text-xs flex items-center gap-1">
              © {new Date().getFullYear()} {siteName}. Made with
              <Heart className="w-3 h-3 text-red-400 fill-red-400 mx-0.5" />
              in India
            </p>
            <div className="flex items-center gap-3 sm:gap-5 text-[11px] text-white/40">
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Verified Providers</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> 24/7 Support</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Pan India</span>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
